import { AutoComplete, Button, Checkbox, Col, Form, Input, Modal, Row, Select } from 'antd';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataKey, SpecialCharacters, TypeList } from "../../Constants/DataModelToolConstants";
import { pickAFileSliceDispatch, questionModalSliceDispatch, questionViewerSliceDispatch } from "../../Constants/ReduxConstants";
import { ReduxHelpers } from "../../Helpers/ReduxHelpers";
import FieldTypeDropDown from ".././FieldTypeDropDown/FieldTypeDropDown";
import QuestionOptionsModal from ".././QuestionOptionsModal/QuestionOptionsModal";
import QuestionServiceQuestionBrandModal from '../QuestionServiceQuestionBrandModal/QuestionServiceQuestionBrandModal';

function QuestionModal() {
    const currentQuestions = useSelector((state: any) => state.pickAFile.questions);
    const currentActions = useSelector((state: any) => state.pickAFile.actions);
    const currentRules = useSelector((state: any) => state.pickAFile.rules);
    const currentLayout = useSelector((state: any) => state.pickAFile.layout);
    const currentServiceQuestions = useSelector((state: any) => state.pickAFile.serviceQuestions);
    const currentServiceQuestionBrand = useSelector((state: any) => state.pickAFile.serviceBrandQuestions);
    const allServices = useSelector((state: any) => state.pickAFile.serviceList);

    const open = useSelector((state: any) => state.questionModal.open);
    const questionToAdd = useSelector((state: any) => state.questionModal.questionToAdd);

    const addModal = useSelector((state: any) => state.questionViewer.addModal);
    const editModal = useSelector((state: any) => state.questionViewer.editModal);
    const currentQuestion = useSelector((state: any) => state.questionViewer.currentQuestion);
    const indexOverride = useSelector((state: any) => state.questionViewer.indexOverride);

    const servicesDropDown = allServices.map(s => { return ({ key: s.serviceId, label: s.serviceName, value: s.serviceId }) }) ?? [];
    const pendingServices = useSelector((state: any) => state.questionModal.pendingServices);

    const selectedQuestion = useSelector((state: any) => state.standardQuestionViewer.selectedQuestion);

    const standardQuestionLayout = useSelector((state: any) => state.standardQuestionViewer.layout);

    const dispatch = useDispatch()

    const { CreateContentsFromStates } = ReduxHelpers();

    const [form] = Form.useForm();

    // Use questionToAdd as single source of truth for all form fields

    //Reset form fields when modal opens or questionToAdd changes
    useEffect(() => {
        // Populate form fields when modal opens or question changes
        if (open && questionToAdd) {
            form.setFieldsValue({ 
                'Data Key': questionToAdd.dataKey,
                'Select Services': pendingServices 
            });
        }
    }, [questionToAdd, open, pendingServices, form]);

    let questionLowerCaseDataKeys = currentQuestions.map(q => {
        return q.dataKey.toLowerCase();
    });
    let currentQuestionIds = currentQuestions.map(q => {
        return q.questionId;
    });
    let adder = AddAQuestion;
    let editor = EditAQuestion;

    //Check to see if the selectedType exists and auto-fill it if we need to
    //This is to avoid editing every single JSON file to add the missing 'selectedType' to each file.
    //Upon save the JSON file will get updated with the selectedType property
    if (!questionToAdd.selectedType) {

        //Find the matching item from the TypeList
        let match = TypeList.find(p => p.value == questionToAdd.type)

        if (match) {
            questionToAdd.selectedType = match?.text
        }
    }

    questionToAdd.questionId = questionToAdd.questionId ? questionToAdd.questionId : indexOverride;

    const onServiceDropDownChange = (value) => {
        dispatch({ type: questionModalSliceDispatch.setPendingServices, payload: value });
    }

    function AddAQuestion(oldList, newQuestion) {
        let listToUse;
        if (oldList) {
            listToUse = oldList;
        } else {
            listToUse = currentQuestions
        }

        listToUse.push(newQuestion)

        dispatch({ type: pickAFileSliceDispatch.setQuestions, payload: listToUse })
        dispatch({ type: questionViewerSliceDispatch.setQuestionSearchResults, payload: listToUse })

        const filteredServices = currentServiceQuestions.filter(s => s.questionId !== currentQuestion.questionId);
        const mappedPendingServices = pendingServices.map(s => ({ assistanceServiceId: s, questionId: questionToAdd.questionId }));

        let combined = filteredServices.concat(mappedPendingServices)
        dispatch({ type: pickAFileSliceDispatch.setServiceQuestions, payload: combined })

        //Add question to layout if it has a tab and category (standard questions).
        let layoutToUse = currentLayout
        if (standardQuestionLayout?.length > 0) {
            let matchIndex = currentLayout.findIndex(l => l.tab == standardQuestionLayout.tab && l.name == standardQuestionLayout.name)

            if (matchIndex > -1) {
                let match = currentLayout[matchIndex]

                match.fields = standardQuestionLayout.fields

                layoutToUse.splice(matchIndex, 1, match)
            }
        }

        dispatch({ type: pickAFileSliceDispatch.setLayout, payload: layoutToUse })

        CreateContentsFromStates({ questions: listToUse, serviceQuestions: combined, layout: layoutToUse });
    }

    function EditAQuestion(oldId, newQuestion) {
        let oldQuestion = currentQuestions.filter(question => question.questionId == oldId)[0];
        let filteredQuestions = DeleteAQuestion(oldId, false)
        AddAQuestion(filteredQuestions, newQuestion)


        //Update layout if we need to
        let dataKeyToDeleteFromLayout = oldQuestion.dataKey;

        for (const layout of currentLayout) {
            const isMatching = (element) => element === dataKeyToDeleteFromLayout;

            let indexFound = layout.fields.findIndex(isMatching)

            if (indexFound > -1) {
                layout.fields[indexFound] = newQuestion.dataKey
            }
        }

        //Update the question id for services.
        const filteredServices = currentServiceQuestions.filter(s => s.questionId !== currentQuestion.questionId);
        const mappedPendingServices = pendingServices.map(s => ({ assistanceServiceId: s, questionId: questionToAdd.questionId }));

        let combined = filteredServices.concat(mappedPendingServices)

        //Update the question data key for rules.
        const filteredAllRules = currentRules.filter(r => r.conditions.all?.some(x => x.fact != oldQuestion.dataKey));
        const filteredAnyRules = currentRules.filter(r => r.conditions.any?.some(x => x.fact != oldQuestion.dataKey));

        var newRules = filteredAllRules.concat(filteredAnyRules);

        const updatedAllRules = currentRules.filter(r => r.conditions.all?.some(x => x.fact == oldQuestion.dataKey));
        const updatedAnyRules = currentRules.filter(r => r.conditions.any?.some(x => x.fact == oldQuestion.dataKey));

        updatedAllRules.forEach(r => r.conditions.all?.forEach(f => f.fact = newQuestion.dataKey));
        updatedAnyRules.forEach(r => r.conditions.any?.forEach(f => f.fact = newQuestion.dataKey));

        var updatedRules = updatedAllRules.concat(updatedAnyRules);

        newRules = newRules.concat(updatedRules);

        //Update the question data key for actions.
        var actionsToUpdate = currentActions.filter(a => a.params.some(x => x == oldQuestion.dataKey));

        var actionsToRemove = new Set(actionsToUpdate);

        var filteredActions = currentActions.filter(a => !actionsToRemove.has(a));

        actionsToUpdate.forEach(a => a.params.forEach(function (item, index) {
            if (item == oldQuestion.dataKey) {
                a.params[index] = newQuestion.dataKey
            }
        }));

        filteredActions = filteredActions.concat(actionsToUpdate);

        dispatch({ type: pickAFileSliceDispatch.setRules, payload: newRules })
        dispatch({ type: pickAFileSliceDispatch.setActions, payload: filteredActions })
        dispatch({ type: pickAFileSliceDispatch.setServiceQuestions, payload: combined })

        CreateContentsFromStates({ questions: filteredQuestions, serviceQuestions: combined, rules: newRules, actions: filteredActions });
    }

    function DeleteAQuestion(idToDelete, deleteFromLayout) {
        let currentQuestionDetail = currentQuestions.filter(question => question.questionId == parseInt(idToDelete) || question.questionId == idToDelete)[0];
        let filteredQuestions = currentQuestions.filter(question => question.questionId != parseInt(idToDelete) || question.questionId != idToDelete);

        dispatch({ type: pickAFileSliceDispatch.setQuestions, payload: filteredQuestions })
        dispatch({ type: questionViewerSliceDispatch.setQuestionSearchResults, payload: filteredQuestions })

        let filteredRules = currentRules;
        let filteredServiceQuestions = currentServiceQuestions;
        let filteredServiceQuestionBrands = currentServiceQuestionBrand;

        //We deleted the question so blow it out of all layouts and actions too
        if (deleteFromLayout) {
            let dataKeyToDeleteFromLayout = currentQuestionDetail.dataKey;

            for (const layout of currentLayout) {
                layout.fields = layout.fields.filter(field => field !== dataKeyToDeleteFromLayout)
            }
            for (const action of currentActions) {
                action.params = action.params.filter(field => field !== dataKeyToDeleteFromLayout)
            }
            for (const rule of filteredRules) {
                if (rule.conditions.all) {
                    rule.conditions.all = rule.conditions.all.filter(condition => condition.fact !== dataKeyToDeleteFromLayout)
                }
                if (rule.conditions.any) {
                    rule.conditions.any = rule.conditions.any.filter(condition => condition.fact !== dataKeyToDeleteFromLayout)
                }
            }
            //Clean up any empty rules
            filteredRules = filteredRules.filter(
                rules => rules.conditions.all && rules.conditions.all.length !== 0 ||
                    rules.conditions.any && rules.conditions.any.length !== 0)

            dispatch({ type: pickAFileSliceDispatch.setRules, payload: filteredRules })

            //Remove any serviceQuestions or serviceQuestionsBrand that contain the question
            filteredServiceQuestions = filteredServiceQuestions.filter(sq => sq.questionId != parseInt(idToDelete))

            dispatch({ type: pickAFileSliceDispatch.setServiceQuestions, payload: filteredServiceQuestions })

            filteredServiceQuestionBrands = filteredServiceQuestionBrands.filter(sqb => sqb.questionId != parseInt(idToDelete))

            dispatch({ type: pickAFileSliceDispatch.setServiceBrandQuestions, payload: filteredServiceQuestionBrands })
        }

        CreateContentsFromStates({ questions: filteredQuestions, serviceQuestions: filteredServiceQuestions });

        return filteredQuestions;
    }

    const onSubmit = () => {
        //Set required and hidden to use 0 or 1.
        questionToAdd.required = questionToAdd.required ? 1 : 0;
        questionToAdd.hidden = questionToAdd.hidden ? 1 : 0;

        //if (questions.)
        //We are adding a new one
        if (addModal) {
            updateServices();
            adder(null, questionToAdd)
        }

        //We are editing one
        if (editModal) {
            updateServices();
            editor(currentQuestion.questionId, questionToAdd)
        }

        dispatch({ type: questionModalSliceDispatch.setOpen, payload: false })
    }

    const updateServices = () => {
        // Combine current pending services to all services for other questions
        const filteredServices = currentServiceQuestions.filter(s => s.questionId !== currentQuestion.questionId);
        const mappedPendingServices = pendingServices.map(s => ({ assistanceServiceId: s, questionId: questionToAdd.questionId }));

        dispatch({ type: pickAFileSliceDispatch.setServiceQuestions, payload: [...filteredServices, ...mappedPendingServices] });
    }

    const onCancel = () => {
        dispatch({ type: questionModalSliceDispatch.setOpen, payload: false })
    }

    const onChange = (event) => {
        form.setFields([
            {
                name: 'Max Length',
                errors: []
            },
            {
                name: 'Type',
                errors: []
            }
        ]);

        if (event.target?.id == "listName") {
            questionToAdd.listName = event.target.value
        }

        //NOTE: Change questionIds to integers
        let valueToUse = event.target.value;
        if (event.target.id == "questionId") {
            valueToUse = parseInt(event.target.value);
        }
        if (event.target?.id == "maxLength" || event.target?.id == "minLength" || event.target?.id == "mergeOrder") {
            valueToUse = parseInt(event.target.value);
        }

        // Checkboxes need to use event.target.checked as event.target.value is always ""
        if (event.target.type === "checkbox") {
            valueToUse = event.target.checked;
        }

        dispatch({ type: questionModalSliceDispatch.setQuestionToAdd, payload: { ...questionToAdd, [event.target.id]: valueToUse } })
    }

    const dataFieldChanged = (event) => {
        dispatch({ type: questionModalSliceDispatch.setQuestionToAdd, payload: { ...questionToAdd, "dataKey": event } })
    }

    const OptionsAdder = (allOptions) => {
        let displayOptions = allOptions.map(al => { return al.option })
        let valueOptions = allOptions.map(al => { return al.value })

        questionToAdd.options = displayOptions
        questionToAdd.values = valueOptions

        dispatch({ type: questionModalSliceDispatch.setQuestionToAdd, payload: questionToAdd })
    }

    let disableOptions = true;
    let disableMarkdown = true;

    switch (questionToAdd.selectedType) {
        case "dropdownlist":
        case "radiogrouplist":
        case "radiogroup":
        case "radiogroup-vertical":
        case "checkboxlist":
            disableOptions = false;
            break;
        case "markdown":
        case "checkbox-attestation":
        case "html":
            disableMarkdown = false;
            break;
        default:
            break;
    }

    return (
        <Modal
            title="Question Details"
            footer={null}
            closable={false}
            open={open}
            destroyOnClose={true}
            onCancel={onCancel}
            width="90vw"
            style={{ top: 20, maxWidth: '1200px', boxShadow: 'none' }}
            bodyStyle={{ 
                maxHeight: 'calc(100vh - 200px)', 
                overflowY: 'auto', 
                overflowX: 'hidden',
                boxShadow: 'none'
            }}
        >
            <Form
                form={form}
                onFinish={onSubmit}
                preserve={false}
                key={questionToAdd?.questionId || 'new'}
            >
                <Row gutter={[48, 48]}>
                    <Col span={12}>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length <= 0) {
                                        reject('A Question ID is required.');
                                    }
                                    if (value?.length > 19) {
                                        reject('The max length cannot exceed 19.');
                                    }
                                    if (currentQuestionIds?.filter(x => x == value).length == 1) {
                                        reject('There is already a question with Question ID.' + value + '.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Question ID" label="Question ID">
                            <Input id="questionId" type="number" placeholder='Question ID' defaultValue={questionToAdd.questionId} onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length > 10) {
                                        reject('The max length cannot exceed 10.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Field ID" label="Field ID">
                            <Input id="fieldId" type="number" placeholder='Field ID' defaultValue={questionToAdd.fieldId} onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length > 10) {
                                        reject('The max length cannot exceed 10.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Form ID" label="Form ID">
                            <Input id="formId" type="number" placeholder='Form ID' defaultValue={questionToAdd.formId} onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{ required: true }, {
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length > 150) {
                                        reject('The max length cannot exceed 150.');
                                    }
                                    if (/\s{2,}/.test(value)) {
                                        reject('The data key contains two or more concurrent spaces.');
                                    }
                                    if (value?.startsWith(" ") || value?.endsWith(" ")) {
                                        reject('Please remove any white space from the begining and/or ending of the data key.');
                                    }
                                    if (SpecialCharacters.some(x => value.includes(x))) {
                                        reject('Please exclude any special characters outside of "_" from the data key.');
                                    }
                                    if (questionLowerCaseDataKeys?.filter(x => x == questionToAdd.dataKey.toLowerCase()).length == 1 && currentQuestion.dataKey.toLowerCase() != questionToAdd.dataKey.toLowerCase()
                                        || questionLowerCaseDataKeys?.filter(x => x == selectedQuestion.dataField?.toLowerCase()).length == 1) {
                                        reject('There is already a question with the data key ' + value + '.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} required name="Data Key" label="Data Key" initialValue={questionToAdd.dataKey}>
                            <AutoComplete
                                id="dataKey"
                                placeholder='Data Key'
                                dropdownMatchSelectWidth={true}
                                options={DataKey.map(k => ({ label: k, value: k }))}
                                onChange={dataFieldChanged}
                            />
                        </Form.Item>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length > 500) {
                                        reject('The max length cannot exceed 500.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Label" label="Label">
                            <Input id="label" placeholder='Label' defaultValue={questionToAdd.label} onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length <= 0 &&
                                        currentServiceQuestionBrand.filter(sb => sb.questionId == questionToAdd.questionId)?.length <= 0) {
                                        reject('A service or service brand combination is required.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Select Services" label="Select Services">
                            <Select
                                placeholder='Select Services'
                                mode="multiple"
                                options={servicesDropDown}
                                defaultValue={pendingServices}
                                onChange={onServiceDropDownChange}
                                id="services"
                            />
                        </Form.Item>
                        <Form.Item>
                            <QuestionServiceQuestionBrandModal />
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="required" defaultChecked={Boolean(questionToAdd.required)} onChange={onChange}>Is Required?</Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="hidden" defaultChecked={Boolean(questionToAdd.hidden)} onChange={onChange}>Is Hidden?</Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="sensitive" defaultChecked={Boolean(questionToAdd.sensitive)} onChange={onChange}>Is Sensitive?</Checkbox>
                        </Form.Item>
                        <Form.Item>
                            <Checkbox id="readonly" defaultChecked={Boolean(questionToAdd.readonly)} onChange={onChange}>Is Read Only?</Checkbox>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <FieldTypeDropDown id="selectedType" defaultValue={questionToAdd.selectedType} form={form} />
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (!disableOptions && value == undefined || value?.length == 0) {
                                        reject('A list name is required.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="List Name" label="List Name">
                            <Input id="listName" placeholder='List Name' defaultValue={questionToAdd.listName} disabled={disableOptions} onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="Options" label="Options">
                            {<QuestionOptionsModal
                                disabled={disableOptions}
                                adder={OptionsAdder}
                            >
                            </QuestionOptionsModal>}
                        </Form.Item>
                        <Form.Item name="Converted Type" label="Converted Type">
                            <Input id="type" defaultValue={questionToAdd.type} disabled />
                        </Form.Item>
                        <Form.Item name="Variant" label="Variant">
                            <Input id="variant" placeholder='Variant' defaultValue={questionToAdd.variant} disabled onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="Default Value" label="Default Value">
                            <Input id="defaultValue" placeholder='Default Value' defaultValue={questionToAdd.defaultValue} onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length > 10) {
                                        reject('The max length cannot exceed 10.');
                                    }
                                    if ((questionToAdd.selectedType == "integer"
                                        || questionToAdd.selectedType == "integer-text"
                                        || questionToAdd.selectedType == "number")
                                        && value > 17) {
                                        reject('Max length for ' + questionToAdd.selectedType + ' cannot exceed 17.');
                                    }
                                    if (questionToAdd.selectedType == "currency" && value > 14) {
                                        reject('Max length for ' + questionToAdd.selectedType + ' cannot exceed 14.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Max Length" label="Max Length">
                            <Input id="maxLength" type="number" placeholder='Max Length' defaultValue={questionToAdd.maxLength} onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length > 10) {
                                        reject('The max length cannot exceed 10.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Min Length" label="Min Length">
                            <Input id="minLength" type="number" placeholder='Min Length' defaultValue={questionToAdd.minLength} onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="Sig" label="Sig">
                            <Input id="sig" placeholder='Sig' defaultValue={questionToAdd.sig} onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="ReadFromDocusign" >
                            <Checkbox id="readFromDocusign" defaultChecked={Boolean(questionToAdd.readFromDocusign)} onChange={onChange}>Read data from Docusign</Checkbox>
                        </Form.Item>
                        <Form.Item name="DocusignCheckboxGroupKey" label="Checkbox Key">
                            <Input id="docusignCheckboxGroupKey" defaultValue={questionToAdd.docusignCheckboxGroupKey} placeholder='docusignCheckboxGroupKey' onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="merge" label="Merge">
                            <Input id="merge" placeholder='merge' defaultValue={questionToAdd.merge} onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="mergeOrder" label="Merge Order">
                            <Input id="mergeOrder" type="number" placeholder='mergeOrder' defaultValue={questionToAdd.mergeOrder} onChange={onChange} />
                        </Form.Item>
                        <Form.Item rules={[{
                            validator(rule, value) {
                                return new Promise((resolve, reject) => {
                                    if (value?.length > 10) {
                                        reject('The max length cannot exceed 10.');
                                    }
                                    else {
                                        resolve(undefined);
                                    }
                                })
                            }
                        }]} name="Sequence" label="Sequence">
                            <Input id="sequence" type="number" placeholder='Sequence' defaultValue={questionToAdd.sequence} onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="Markdown" label="Markdown">
                            <textarea id="markdown" placeholder='Markdown' defaultValue={questionToAdd.markdown} disabled={disableMarkdown} onChange={onChange} />
                        </Form.Item>
                        <Form.Item name="Shape" label="Shape">
                            <Input id="shape" placeholder='Shape' defaultValue={questionToAdd.shape} disabled onChange={onChange} />
                        </Form.Item>
                    </Col>
                </Row>
                <div style={{ display: 'flex', justifyContent: 'end', paddingTop: '1em' }}>
                    <Form.Item>
                        <Button onClick={onCancel}>
                            Cancel
                        </Button>
                    </Form.Item>
                    <Form.Item>
                        <Button htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    )
}

export default QuestionModal